import { getFreeGiftCartUpdates } from './free-gift-rules.js';

const GUARD_SOURCE = 'free-gift-cart-guard';
const CHECKOUT_SELECTOR =
  '#checkout, [name="checkout"], a[href*="/checkout"], .cart__mobile-sticky-checkout, .cart-glass-actions__checkout';

export function buildFreeGiftUpdatePayload(cart, sectionIds = [], sectionsUrl = '/') {
  const updates = getFreeGiftCartUpdates(cart);
  if (!Object.keys(updates).length) return null;

  const payload = {
    updates,
    sections_url: sectionsUrl,
  };

  if (sectionIds.length) {
    payload.sections = [...new Set(sectionIds)].join(',');
  }

  return payload;
}

class FreeGiftCartGuard {
  #timer;
  #isSyncing = false;

  constructor() {
    document.addEventListener('cart:update', this.#requestSync);
    document.addEventListener('cart:updated', this.#requestSync);
    document.addEventListener('click', this.#handleCheckoutClick, true);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#requestSync, { once: true });
    } else {
      this.#requestSync();
    }

    window.addEventListener('pageshow', this.#requestSync);
  }

  #requestSync = () => {
    if (this.#isSyncing) return;
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.syncCart(), 120);
  };

  async syncCart() {
    if (this.#isSyncing) return { corrected: false, cart: null };
    this.#isSyncing = true;

    try {
      const cart = await this.#fetchCart();
      const payload = buildFreeGiftUpdatePayload(cart, this.#getSectionIds(), window.location.pathname);

      if (!payload) return { corrected: false, cart };

      const response = await fetch(this.#getCartUpdateUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Free gift cart correction failed.');

      const updatedCart = await response.json();
      this.#dispatchCartUpdated(updatedCart);

      return { corrected: true, cart: updatedCart };
    } catch (error) {
      console.error(error);
      return { corrected: false, cart: null, error };
    } finally {
      this.#isSyncing = false;
    }
  }

  #handleCheckoutClick = async (event) => {
    const trigger = event.target?.closest?.(CHECKOUT_SELECTOR);
    if (!trigger || trigger.dataset.freeGiftGuardBypass === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#setCheckoutTriggerBusy(trigger, true);

    const result = await this.syncCart();

    this.#setCheckoutTriggerBusy(trigger, false);
    if (result.corrected) return;

    this.#continueCheckout(trigger);
  };

  #continueCheckout(trigger) {
    if (trigger instanceof HTMLAnchorElement) {
      window.location.href = trigger.href;
      return;
    }

    const form = trigger.form || trigger.closest('form');
    if (!form) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(trigger);
      return;
    }

    form.submit();
  }

  #setCheckoutTriggerBusy(trigger, busy) {
    if (!('disabled' in trigger)) return;
    trigger.disabled = busy;
    trigger.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  async #fetchCart() {
    const response = await fetch(this.#getCartJsonUrl(), {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Cart could not be loaded.');

    return response.json();
  }

  #dispatchCartUpdated(cart) {
    const sections = cart.sections || {};

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: GUARD_SOURCE,
          data: {
            source: GUARD_SOURCE,
            itemCount: cart.item_count,
            sections,
          },
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, sections },
      })
    );

    if (!Object.keys(sections).length) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  #getSectionIds() {
    return [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
          .map((item) => item.dataset.sectionId)
          .filter(Boolean)
      ),
    ];
  }

  #getCartJsonUrl() {
    const root = window.StorefrontLocale?.root || window.Shopify?.routes?.root || '/';
    return `${root}cart.js`;
  }

  #getCartUpdateUrl() {
    if (window.Theme?.routes?.cart_update_url) return window.Theme.routes.cart_update_url;

    const root = window.StorefrontLocale?.root || window.Shopify?.routes?.root || '/';
    return `${root}cart/update.js`;
  }
}

if (typeof window !== 'undefined' && !window.__freeGiftCartGuard) {
  window.__freeGiftCartGuard = new FreeGiftCartGuard();
}
